import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useAppDispatch } from '@/redux/Store';
import { toast } from 'react-toastify';
import { LoadingSpinner } from '@/components';
import {
  fetchEarnOverview,
  fetchEarnConfig,
  fetchEarnLedger,
  updateEarnConfigRow,
  type EarnOverview,
  type EarnConfigRow,
  type EarnLedgerRow,
} from '@/redux/thunks/adminAchievementEarnThunks';
import './page.scss';

type StatusFilter = 'all' | EarnLedgerRow['status'];

const PAGE_SIZE = 50;

const AdminAchievementEarnPage: React.FC = () => {
  const dispatch = useAppDispatch();
  const [overview, setOverview] = useState<EarnOverview | null>(null);
  const [config, setConfig] = useState<EarnConfigRow[]>([]);
  const [ledger, setLedger] = useState<EarnLedgerRow[]>([]);
  const [ledgerTotal, setLedgerTotal] = useState(0);
  const [ledgerOffset, setLedgerOffset] = useState(0);
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('all');
  const [loading, setLoading] = useState(false);
  const [savingType, setSavingType] = useState<string | null>(null);

  const loadLedger = useCallback(
    async (offset: number, status: StatusFilter) => {
      const query: Parameters<typeof fetchEarnLedger>[0] = { limit: PAGE_SIZE, offset };
      if (status !== 'all') query.status = status;
      const result = await dispatch(fetchEarnLedger(query)).unwrap();
      setLedger(result.rows);
      setLedgerTotal(result.total);
      setLedgerOffset(result.offset);
    },
    [dispatch],
  );

  const loadAll = useCallback(async () => {
    setLoading(true);
    try {
      const [ov, cfg] = await Promise.all([
        dispatch(fetchEarnOverview()).unwrap(),
        dispatch(fetchEarnConfig()).unwrap(),
      ]);
      setOverview(ov);
      setConfig(cfg.rows);
      await loadLedger(0, statusFilter);
    } catch (err: any) {
      toast.error(typeof err === 'string' ? err : 'Failed to load earn admin data');
    } finally {
      setLoading(false);
    }
  }, [dispatch, loadLedger, statusFilter]);

  useEffect(() => {
    loadAll();
  }, [loadAll]);

  const handleToggleEnabled = async (row: EarnConfigRow) => {
    setSavingType(row.achievementType);
    try {
      const next = !row.enabled;
      const res = await dispatch(
        updateEarnConfigRow({ achievementType: row.achievementType, enabled: next }),
      ).unwrap();
      setConfig((prev) => prev.map((r) => (r.achievementType === row.achievementType ? res.row : r)));
      toast.success(`${row.achievementType}: ${next ? 'enabled' : 'disabled'}`);
    } catch (err: any) {
      toast.error(typeof err === 'string' ? err : 'Update failed');
    } finally {
      setSavingType(null);
    }
  };

  const handlePayoutChange = async (row: EarnConfigRow, raw: string) => {
    const value = parseFloat(raw);
    if (Number.isNaN(value) || value < 0) {
      toast.error('Payout must be a non-negative number');
      return;
    }
    if (value === row.payoutValue) return;
    setSavingType(row.achievementType);
    try {
      const res = await dispatch(
        updateEarnConfigRow({ achievementType: row.achievementType, payoutValue: value }),
      ).unwrap();
      setConfig((prev) => prev.map((r) => (r.achievementType === row.achievementType ? res.row : r)));
      toast.success(`${row.achievementType}: payout set to ${value}`);
    } catch (err: any) {
      toast.error(typeof err === 'string' ? err : 'Update failed');
    } finally {
      setSavingType(null);
    }
  };

  const handleFilterChange = (next: StatusFilter) => {
    setStatusFilter(next);
    loadLedger(0, next).catch((err: any) => toast.error(err?.message ?? 'Reload failed'));
  };

  const handlePage = (dir: 'prev' | 'next') => {
    const next = dir === 'next' ? ledgerOffset + PAGE_SIZE : Math.max(0, ledgerOffset - PAGE_SIZE);
    loadLedger(next, statusFilter).catch((err: any) => toast.error(err?.message ?? 'Reload failed'));
  };

  const totalsRow = useMemo(() => {
    if (!overview) return null;
    const t = overview.totals;
    return [
      { label: 'Paid', count: t.paid?.count ?? 0, klv: t.paid?.sumKlv ?? 0, cls: 'success' },
      { label: 'Failed', count: t.failed?.count ?? 0, klv: t.failed?.sumKlv ?? 0, cls: 'error' },
      { label: 'Cancelled', count: t.cancelled?.count ?? 0, klv: t.cancelled?.sumKlv ?? 0, cls: 'warning' },
      { label: 'Frozen', count: t.frozen?.count ?? 0, klv: t.frozen?.sumKlv ?? 0, cls: 'warning' },
    ];
  }, [overview]);

  return (
    <div className="admin-earn-page">
      <div className="admin-earn-container">
        <h1>Achievement Earn</h1>
        <p className="subtitle">
          @klyx-rewards KLV payouts for earned achievements. See{' '}
          <code>docs/RUNBOOK.md § Achievement earn ops</code> for funding + alerts.
        </p>

        {loading && !overview && <LoadingSpinner size="large" message="Loading..." />}

        {overview && (
          <div className="overview-grid">
            <div className={`stat-card ${overview.serviceReady ? '' : 'warning'}`}>
              <div className="stat-label">Service status</div>
              <div className="stat-value">{overview.serviceReady ? 'Ready' : 'Not initialized'}</div>
              <div className="stat-sub">
                {overview.serviceReady
                  ? overview.walletAddress
                  : 'Run bootstrap:klyx-wallets + fund @klyx-rewards'}
              </div>
            </div>
            <div className={`stat-card ${overview.balanceKlv !== null && overview.balanceKlv < 100 ? 'error' : ''}`}>
              <div className="stat-label">Wallet balance</div>
              <div className="stat-value">
                {overview.balanceKlv !== null ? `${overview.balanceKlv.toFixed(2)} KLV` : 'n/a'}
              </div>
              <div className="stat-sub">Hot wallet, 1 of 1 — refill from treasury</div>
            </div>
            <div className="stat-card">
              <div className="stat-label">Lifetime paid</div>
              <div className="stat-value">{overview.lifetimePaidKlv.toFixed(2)} KLV</div>
              <div className="stat-sub">{overview.totals.paid?.count ?? 0} payouts</div>
            </div>
            <div className="stat-card">
              <div className="stat-label">Last 24h paid</div>
              <div className="stat-value">{overview.last24hPaidKlv.toFixed(2)} KLV</div>
              <div className="stat-sub">Rolling window</div>
            </div>
          </div>
        )}

        {totalsRow && (
          <div className="totals-row">
            {totalsRow.map((t) => (
              <div key={t.label} className={`totals-cell ${t.cls}`}>
                <div className="totals-label">{t.label}</div>
                <div className="totals-value">
                  {t.count} <span className="muted">· {t.klv.toFixed(2)} KLV</span>
                </div>
              </div>
            ))}
          </div>
        )}

        <section className="section">
          <h2>Earn config</h2>
          <p className="section-sub">
            Toggle an achievement off or adjust its fixed payout. pct_* types land in a follow-up PR.
          </p>
          <div className="config-table">
            <table>
              <thead>
                <tr>
                  <th>Achievement</th>
                  <th>Type</th>
                  <th>Payout</th>
                  <th>Enabled</th>
                </tr>
              </thead>
              <tbody>
                {config.map((row) => (
                  <tr key={row.achievementType} className={row.enabled ? '' : 'row-disabled'}>
                    <td>
                      <code>{row.achievementType}</code>
                    </td>
                    <td>{row.payoutType}</td>
                    <td>
                      {row.payoutType === 'fixed_klv' ? (
                        <input
                          type="number"
                          min="0"
                          step="0.1"
                          defaultValue={row.payoutValue}
                          disabled={savingType === row.achievementType}
                          onBlur={(e) => handlePayoutChange(row, e.target.value)}
                        />
                      ) : (
                        <span className="muted">
                          {row.payoutValue}% <em>(not yet wired)</em>
                        </span>
                      )}
                    </td>
                    <td>
                      <button
                        className={`toggle ${row.enabled ? 'on' : 'off'}`}
                        disabled={savingType === row.achievementType}
                        onClick={() => handleToggleEnabled(row)}
                      >
                        {row.enabled ? 'ON' : 'OFF'}
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>

        <section className="section">
          <h2>Payout ledger</h2>
          <div className="filter-row">
            <label>Status:</label>
            {(['all', 'paid', 'failed', 'cancelled', 'frozen'] as StatusFilter[]).map((s) => (
              <button
                key={s}
                className={`filter-chip ${statusFilter === s ? 'active' : ''}`}
                onClick={() => handleFilterChange(s)}
              >
                {s}
              </button>
            ))}
          </div>
          <div className="ledger-table">
            <table>
              <thead>
                <tr>
                  <th>When</th>
                  <th>User</th>
                  <th>Achievement</th>
                  <th>Payout</th>
                  <th>Status</th>
                  <th>Tx</th>
                </tr>
              </thead>
              <tbody>
                {ledger.map((row) => (
                  <tr key={row.id}>
                    <td>{new Date(row.createdAt).toLocaleString()}</td>
                    <td>{row.username ?? row.appUserId.slice(0, 8)}</td>
                    <td>
                      <code>{row.achievementType}</code>
                    </td>
                    <td>{row.payoutKlv.toFixed(4)} KLV</td>
                    <td>
                      <span className={`status-pill ${row.status}`}>{row.status}</span>
                    </td>
                    <td>
                      {row.txHash ? (
                        <code title={row.notes ?? undefined}>{row.txHash.slice(0, 10)}…</code>
                      ) : (
                        <span className="muted" title={row.notes ?? undefined}>
                          {row.notes ? row.notes.slice(0, 40) + '…' : '—'}
                        </span>
                      )}
                    </td>
                  </tr>
                ))}
                {ledger.length === 0 && (
                  <tr>
                    <td colSpan={6} className="empty">
                      No ledger rows match this filter.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
          <div className="pagination">
            <button disabled={ledgerOffset === 0} onClick={() => handlePage('prev')}>
              ← Prev
            </button>
            <span>
              {ledgerOffset + 1}–{Math.min(ledgerOffset + ledger.length, ledgerTotal)} of {ledgerTotal}
            </span>
            <button
              disabled={ledgerOffset + ledger.length >= ledgerTotal}
              onClick={() => handlePage('next')}
            >
              Next →
            </button>
          </div>
        </section>
      </div>
    </div>
  );
};

export default AdminAchievementEarnPage;
